<script setup lang="ts">
import type { GetDonationBoxResult } from '~/types/donations'
import type { FetchError } from 'ofetch'
import { getMetaTags } from '~/utils/meta'
import { useLivePreview } from '@payloadcms/live-preview-vue'
import type { DonationBox } from '#shared/types/donations'

definePageMeta({
  layout: false
})

defineRouteRules({
  prerender: false
})

const { t, baseKey } = useI18n('pages.actie.donation-box')

const route = useRoute('actie-slug')
const isPreview = computed(() => !!route.query.preview)

if (!route.params.slug) {
  throw createError({
    status: 404
  })
}

const { data: initialData, error } = await useFetch<GetDonationBoxResult>(
  '/api/donation-box/fetch',
  {
    query: {
      slug: route.params.slug,
      preview: route.query.preview ?? ''
    }
  }
)
if (!initialData.value) {
  throw createError({
    status: 404
  })
}

let donationBoxData: Ref<DonationBox>
if (isPreview.value) {
  const config = useRuntimeConfig()
  const { data: tempData } = useLivePreview<DonationBox>({
    initialData: initialData.value!.donationBox,
    serverURL: config.public.cmsUrl
  })
  donationBoxData = tempData
} else {
  donationBoxData = toRef(initialData.value!.donationBox)
}

const data = computed<GetDonationBoxResult | undefined>(() => {
  if (!donationBoxData.value || !initialData.value) {
    return undefined
  }

  return {
    donationBox: donationBoxData.value,
    donationStatistics: initialData.value.donationStatistics
  }
})

watch(
  error,
  (newValue) => {
    if (newValue) {
      const error = newValue as FetchError
      throw createError({
        status: error.status
      })
    }
  },
  { immediate: true }
)

useSeoMeta(
  getMetaTags(
    {
      title: t('.meta.title', {
        name: data.value?.donationStatistics.userName
      })
    },
    data.value?.donationBox.coverPhoto.url,
    baseKey
  )
)

useSchemaOrg(
  getSchemaOrg(
    {
      title: t('.meta.title', {
        name: data.value?.donationStatistics.userName
      })
    },
    baseKey
  )
)
</script>

<template>
  <div>
    <DonationBox :data :preview="isPreview" />

    <CMSPreviewWidget v-if="isPreview" />
  </div>
</template>

<style scoped></style>
